import { ref, computed } from 'vue'
import type {
  AddOrUpdateItemType,
  BasketWithOptions,
} from '@scayle/storefront-nuxt'
import {
  ExistingItemHandling,
  getFirstAttributeValue,
} from '@scayle/storefront-nuxt'
import { orderBy } from 'es-toolkit'
import {
  findBasketItem,
  basketContainsItem,
  countAvailableBasketItems,
} from '@scayle/storefront-basket/utils'
import type { BasketItemWithCoupon } from './tracking/types'
import { useBasket, useRpcCall } from '#storefront/composables'

import { getFormattedOrderNumber } from '~/utils/product'
import { getDeliveryEstimation } from '#layers/base/utils/date'
import { getDeliveryEstimationInDays } from '#layers/base/utils/shopConfiguration'

import useSmartShopConfiguration from '#layers/base/composables/useSmartShopConfiguration'
import { BASKET_WITH } from '#layers/base/constants/withParameters'
import { useBasketDeliveryEstimate } from '#layers/base/composables/useBasketDeliveryEstimate'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import useSizeDisplayData from '#layers/base/composables/useSizeDisplayData'
import type { CustomData } from '#layers/base/constants/basket'
import type { AgsBasketItem } from '#layers/base/types/basket'

const singletonMap = new Map<string, ReturnType<typeof useAgsBasket>>()

export default (pricePromotionKey = '') => {
  if (singletonMap.has(pricePromotionKey) && import.meta.client) {
    return singletonMap.get(pricePromotionKey)!
  }
  const instance = useAgsBasket(pricePromotionKey)
  singletonMap.set(pricePromotionKey, instance)
  return instance
}

function useAgsBasket(pricePromotionKey: string = '') {
  const addItemsToBasketRpc = useRpcCall('addItemsWithPromotionsToBasket')

  const { getSizeDisplayData } = useSizeDisplayData()
  const { trackRemoveFromBasket, trackAddToBasket } = useTrackingEvents()
  const { getBaskedDeliveryEstimationWithDate } = useBasketDeliveryEstimate()
  const log = useLog('useAgsBasket')
  const shopConfiguration = useSmartShopConfiguration()

  const params = ref<
    BasketWithOptions & {
      orderCustomData?: Record<string, unknown>
    }
  >({
    ...BASKET_WITH,
    ...(pricePromotionKey && { pricePromotionKey }),
    applicablePromotions: true,
  })

  const basket = useBasket({ params }, 'useAgsBasket')

  const deliveryEstimationInDays = computed(() =>
    getDeliveryEstimationInDays(shopConfiguration.data.value),
  )

  const addItems = async (
    items: AddOrUpdateItemType[],
    existingItemHandling: ExistingItemHandling = ExistingItemHandling.REPLACE_EXISTING,
  ) => {
    try {
      const { orderCustomData, ...basketWithOptions } = params.value

      const addItemsParams = {
        items,
        basketWithOptions,
        orderCustomData,
        existingItemHandling,
      }

      await addItemsToBasketRpc(addItemsParams)
      await basket.refresh()
    } catch (e) {
      handleBasketError('addItems', e)
    }
  }

  const _fetch = async () => {
    if (import.meta.server) {
      return
    }

    await Promise.all([shopConfiguration.fetch(), basket.refresh()])
  }

  const updateItemsWithInvalidDeliveryInfo = async () => {
    const itemsToUpdate = basket.data.value?.items
      .map((item) => {
        const variant = item.variant
        const customData = item.customData as CustomData

        const deliveryEstimationDate = customData?.deliveryEstimationDate
        const updatedDeliveryEstimationDate = getDeliveryEstimation(
          variant,
          deliveryEstimationInDays.value,
        )

        if (
          deliveryEstimationDate?.toString().split('T')[0] ===
          updatedDeliveryEstimationDate?.toISOString().split('T')[0]
        ) {
          return
        }

        const deliveryDisplayData = getBaskedDeliveryEstimationWithDate(
          updatedDeliveryEstimationDate,
        )

        const sizeDisplayData = {
          'attribute-2': getSizeDisplayData(variant),
        }

        const newItem: AddOrUpdateItemType = {
          variantId: variant.id,
          quantity: item.quantity,
          // @ts-expect-error Types of property '"attribute-2"' are incompatible due to `displayData` not being well typed.
          displayData: {
            ...deliveryDisplayData,
            ...(sizeDisplayData || {}),
          },
          customData: {
            ...(customData || {}),
            deliveryEstimationDate: updatedDeliveryEstimationDate,
          },
          existingItemHandling: ExistingItemHandling.REPLACE_EXISTING,
        }

        return newItem
      })
      .filter((item) => item !== undefined)

    if (!itemsToUpdate) {
      return
    }

    await addItems(itemsToUpdate)
  }

  const fetch = async () => {
    await _fetch()
    await updateItemsWithInvalidDeliveryInfo()
  }

  if (import.meta.client && basket.status.value !== 'pending') {
    _fetch()
  }

  const removeItem = async (item: BasketItemWithCoupon) => {
    try {
      const { key, product, variant, quantity = 1 } = item

      await basket.removeItemByKey(key)
      await basket.refresh()

      trackRemoveFromBasket({
        product,
        quantity,
        variant,
        coupon: item.promotionCode,
        type: 'delete_cart',
      })
    } catch (error) {
      handleBasketError('removeItemFromBasket', error)
    }
  }

  const changeQuantity = async ({
    quantity,
    item,
  }: {
    quantity: number
    item: BasketItemWithCoupon
  }) => {
    const product = item.product
    const variant = item.variant
    const customData = item.customData as
      | { ordernumber: string; pricePromotionKey?: string }
      | undefined
    const promotionId = item.promotionId
    const currentQuantity = item.quantity
    const quantityIncreased = quantity > currentQuantity

    try {
      await basket.updateItem(item.key, {
        variantId: variant.id,
        quantity,
        ...(customData?.pricePromotionKey && {
          pricePromotionKey: customData.pricePromotionKey,
        }),
        displayData: {
          // @ts-expect-error Types of property '"attribute-2"' are incompatible due to `displayData` not being well typed.
          'attribute-2': getSizeDisplayData(variant),
          ...getBaskedDeliveryEstimationWithDate(
            getDeliveryEstimation(variant, deliveryEstimationInDays.value),
          ),
        },
        customData: {
          ordernumber:
            customData?.ordernumber || getFormattedOrderNumber(product),
          deliveryEstimationDate: getDeliveryEstimation(
            variant,
            deliveryEstimationInDays.value,
          ),
        },
        promotionId,
      })

      if (quantityIncreased) {
        trackAddToBasket({
          product,
          variant,
          quantity: quantity - currentQuantity,
          coupon: item.promotionCode,
          type: 'increase_item_quantity',
        })
      } else {
        trackRemoveFromBasket({
          product,
          variant,
          quantity: currentQuantity - quantity,
          coupon: item.promotionCode,
          type: 'decrease_cart',
        })
      }
    } catch (e) {
      handleBasketError('changeBasketProductQuantity', e)
    }
  }

  const setPricePromotionKey = (newKey: string) => {
    params.value = { ...params.value, pricePromotionKey: newKey }
  }

  const handleBasketError = (logSpace: string, e: unknown) => {
    if (typeof e === 'string' || e instanceof Error) {
      log.space(logSpace).error(e)
    }
  }

  const items = computed<AgsBasketItem[]>(() => {
    return orderBy(
      basket.items.value ?? [],
      [
        (item) =>
          getFirstAttributeValue(item.product?.attributes, 'name')?.label,
        (item) => getFirstAttributeValue(item.variant?.attributes, 'size')?.id,
      ],
      ['asc'],
    )
  })

  const availableItems = computed(() => {
    return items.value.filter(({ status }) => status === 'available')
  })

  const unavailableItems = computed(() => {
    return items.value.filter(({ status }) => status === 'unavailable')
  })

  return {
    data: basket.data,
    count: basket.count,
    status: basket.status,
    cost: basket.cost,
    applicablePromotions: basket.data.value?.applicablePromotions,
    items,
    availableItems,
    unavailableItems,

    refresh: basket.refresh,
    addItem: basket.addItem,
    findItem: findBasketItem,
    contains: basketContainsItem,
    removeFreeGiftItem: basket.removeItemByKey,
    countAvailableBasketItems,

    fetch,
    addItems,
    removeItem,
    changeQuantity,
    setPricePromotionKey,
  }
}
